import Image from 'next/image'

import Breadcrumbs from '@/molecules/Breadcrumb/Breadcrumb'

interface Props {
  title: string
  description?: string
  company?: string
  tag?: string
  stats: {
    title: string
    description: string
  }[]
  featuredImage?: StrapiImageFile
  customerLogo?: StrapiImageFile
}

function IndividualCaseStudyPageHero({
  title,
  description,
  company,
  tag,
  stats,
  featuredImage,
  customerLogo
}: Props) {
  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home', url: '/' },
    { name: 'Customers', url: '/case-study/' },
    { name: company || title, url: '' }
  ]

  return (
    <header className="relative border-b border-[#DEE6F6] bg-linear-to-br from-[#F4F7FE] to-[#E9F0FC]">
      {/* decorative blob */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-44 -top-52 h-[560px] w-[560px] rounded-[46%_54%_60%_40%/50%_44%_56%_50%] bg-blue-10 opacity-70"
      />

      <nav
        aria-label="breadcrumbs"
        className="container | relative z-1 pt-6 text-[13px] text-[#7C87B4]"
      >
        <Breadcrumbs breadcrumbItems={breadcrumbs} />
      </nav>

      <div className="container | relative z-1 grid grid-cols-1 items-center gap-11 py-11 md:pb-14 lg:grid-cols-[1.1fr_.9fr] lg:gap-16">
        {/* left — story intro */}
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-3.5">
            {customerLogo?.url ? (
              <span className="inline-flex items-center rounded-[14px] bg-[#1E2178] px-[18px] py-2.5">
                <Image
                  src={customerLogo.url}
                  alt={customerLogo.alternativeText ?? ''}
                  width={customerLogo.width}
                  height={customerLogo.height}
                  className="h-[22px] w-auto max-w-[150px] object-contain"
                  priority
                />
              </span>
            ) : null}
            {tag ? (
              <span className="inline-block rounded-full border-[1.5px] border-[#D5E2FF] bg-white px-3.5 py-[7px] text-[11.5px] font-bold uppercase tracking-[0.13em] text-[#2563FF]">
                {tag}
              </span>
            ) : null}
          </div>

          <h1 className="font-heading mb-4 text-[28px] font-extrabold leading-[1.14] tracking-tight text-[#131C4F] md:text-[40px] text-balance">
            {title}
          </h1>
          {description ? (
            <p className="text-[15.5px] leading-relaxed text-[#46517A]">
              {description}
            </p>
          ) : null}

          {stats.length > 0 ? (
            <div className="mt-7 flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-[0_2px_10px_rgba(19,28,79,0.06),0_18px_44px_rgba(19,28,79,0.08)] md:flex-row md:items-start md:gap-0">
              {stats.map((item, index) => (
                <div
                  key={item.title}
                  className="flex-1 md:border-l md:border-[#DEE6F6] md:px-6 md:first:border-l-0 md:first:pl-0 md:last:pr-0"
                >
                  <div
                    className={`font-heading text-[34px] font-extrabold leading-none tracking-tight ${
                      index % 2 === 1 ? 'text-[#2563FF]' : 'text-[#1E2178]'
                    }`}
                  >
                    {item.title}
                  </div>
                  <div className="mt-1.5 text-[11.5px] leading-tight text-[#46517A]">
                    {item.description}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* right — featured figure with offset panel */}
        {featuredImage?.url ? (
          <div className="relative mx-auto w-full max-w-[420px] lg:ml-auto lg:mr-0">
            <span
              aria-hidden
              className="absolute -bottom-4 -right-4 left-[26px] top-[26px] rounded-[36px_36px_36px_14px] bg-[#1F2179]"
            />
            <Image
              src={featuredImage.url}
              alt={featuredImage.alternativeText ?? ''}
              width={featuredImage.width}
              height={featuredImage.height}
              className="relative aspect-square w-full rounded-[36px_36px_14px_36px] bg-pastel-blue-300 object-cover object-top shadow-[0_10px_30px_rgba(19,28,79,0.12),0_40px_90px_rgba(19,28,79,0.16)]"
              priority
            />
          </div>
        ) : null}
      </div>
    </header>
  )
}

export default IndividualCaseStudyPageHero
